import { initCoreRuntime } from '@core/runtime';
import { getUserById, getTierProgressById } from '@core/api/users';
import { claimGiftLink } from '@core/api/gifts';
import { pwaGiftLink, pwaInviteLink } from '@core/links';
import { AUTH_EXPIRED_EVENT } from './api/client';
import { logoutSession } from './api/auth';
import { takeGiftClaimCode } from './utils/entryParams';
import { forgetWebPushOnLogout } from '@core/utils/webPush';

// Тонкий вход PWA: cookie-сессия (httpOnly customerSession), слаг — из пути URL,
// профиль — по uuid клиента из localStorage-профиля AuthContext (не секрет).
// Всё остальное — экраны, маршруты, запросы — общее с мини-аппом (ядро): PWA — лишь второй вход.
// Импортируется ПЕРВЫМ в main.tsx — до первого рендера ядра.

const STORAGE_KEY = 'lunit_pwa_customer';

function storedUserId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as { id?: string }).id ?? null;
  } catch {
    return null;
  }
}

function venueSlugFromPath(): string {
  return window.location.pathname.split('/').filter(Boolean)[0] || '';
}

initCoreRuntime({
  kind: 'phone',
  getVenueSlug: venueSlugFromPath,
  getAuthHeaders: () => ({}),
  credentials: 'include',
  onUnauthorized: () => window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT)),
  fetchProfile: async (identity) => {
    const id = identity.userId ?? storedUserId();
    if (!id) return null;
    const profile = await getUserById(id, true);

    // Подарок по ссылке друга (<slug>?gift=<код>) — забираем один раз, когда клиент вошёл; итог покажет
    // экран «Подарки» (как в мини-аппе). Клиент — из cookie-сессии, Telegram ID не нужен.
    const giftClaimCode = takeGiftClaimCode(venueSlugFromPath());
    if (giftClaimCode) {
      claimGiftLink(giftClaimCode)
        .then((result) => {
          localStorage.setItem('pending-gift-claim', JSON.stringify(result));
        })
        .catch((err) => {
          console.warn('[coreRuntime] Gift claim failed:', err);
        });
    }

    return profile;
  },
  fetchTierProgress: async (identity) => {
    const id = identity.userId ?? storedUserId();
    return id ? getTierProgressById(id) : null;
  },
  // Функционал — как в мини-аппе (включая мини-игру). Нет только того, что даёт Telegram: requestContact
  // (номер в PWA — это логин) и haptics. Взамен есть выход из cookie-сессии.
  // Уведомления в браузере — только у PWA (в мини-аппе — Telegram).
  capabilities: { telegram: false, requestContact: false, game: true, logout: true, webPush: true },
  // Ссылки «поделиться» из PWA ведут в PWA заведения: друг без Telegram регистрируется по телефону.
  links: {
    invite: (venue, referralCode) => pwaInviteLink(window.location.origin, venue.slug, referralCode),
    gift: (venue, link) => pwaGiftLink(window.location.origin, venue.slug, link.claimCode),
  },
  logout: async () => {
    // Общее устройство: уведомления этой карточки сюда больше не приходят (пока сессия жива — снимаем на сервере).
    await forgetWebPushOnLogout(storedUserId());
    try {
      await logoutSession();
    } catch {
      // выход не должен зависеть от сети — локальную сессию чистим всё равно
    }
    // AuthContext ловит событие: чистит профиль и кэш react-query, гвард уводит на вход.
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  },
  // Удаление данных (K-025): сессия на сервере уже отозвана — снимаем cookie и уходим на вход по номеру.
  afterDataErased: () => {
    void logoutSession()
      .catch(() => undefined)
      .finally(() => window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT)));
  },
});
